import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { HiMenu } from "react-icons/hi";
import { IoMdClose } from "react-icons/io";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../theme";

const Header = () => {
  const { isDarkMode, toggleDarkMode, colors } =
    useContext(ThemeContext)!;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const toggleRef = useRef<HTMLSpanElement | null>(null);
  const location = useLocation();

  /* ---------------------------------------------------
     Body background + scroll lock
  --------------------------------------------------- */
  useEffect(() => {
    document.body.style.backgroundColor = isDarkMode ? "black" : "white";
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [isDarkMode, menuOpen]);

  /* ---------------------------------------------------
     Close menu
  --------------------------------------------------- */
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  /* ---------------------------------------------------
     Click outside handler
  --------------------------------------------------- */
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        toggleRef.current &&
        !toggleRef.current.contains(target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen, closeMenu]);

  /* ---------------------------------------------------
     Close on route change
  --------------------------------------------------- */
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  /* ---------------------------------------------------
     Menu items
  --------------------------------------------------- */
  const navLinks = [
    { path: "/how-to-play", label: "How to Play" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
    { path: "/terms", label: "Ts&Cs" },
    { path: "/subscription", label: "Subscription" },
  ];

  return (
    <header className={`sticky top-0 z-50 ${colors.bgColor}`}>
      <nav className="mx-auto flex items-center justify-between px-5 py-4 md:px-24">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src={process.env.PUBLIC_URL + "/images/icallont.png"}
            alt="I Call On"
            width={30}
            height={30}
          />
          <span className={`text-2xl font-bold ${colors.textColor}`}>
            I Call On
          </span>
        </Link>
        <button
          onClick={toggleDarkMode}
          className="text-xl ml-4"
          aria-label="Toggle theme"
        >
          {isDarkMode ? "☀️" : "🌙"}
        </button>
        {/* Desktop menu */}
        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`text-lg transition hover:opacity-70 ${location.pathname === link.path
                  ? "font-semibold"
                  : ""
                  } ${colors.textColor}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
          <Link
            to="/download"
            className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 transition"
          >
            Download
          </Link>
        </ul>

        {/* Mobile toggle */}
        <span
          ref={toggleRef}
          className="text-3xl cursor-pointer md:hidden"
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          {menuOpen ? (
            <IoMdClose className={colors.textColor} />
          ) : (
            <HiMenu className={colors.textColor} />
          )}
        </span>
      </nav>

      {/* Mobile menu */}
      <ul
        ref={menuRef}
        className={`md:hidden fixed left-0 w-full px-7 pb-10 pt-6 transition-all duration-300 ease-in-out ${menuOpen
          ? `top-[64px] opacity-100 ${colors.bgSecondaryColor}`
          : "top-[-400px] opacity-0"
          }`}
      >
        {navLinks.map((link) => (
          <li key={link.path} className="my-5">
            <Link
              to={link.path}
              className={`text-xl ${colors.textColor}`}
            >
              {link.label}
            </Link>
          </li>
        ))}

        <Link
          to="/download"
          className="mt-4 inline-block rounded-lg bg-blue-600 px-6 py-3 text-white"
        >
          Download
        </Link>


      </ul>
    </header>
  );
};

export default Header;
